/**
 * knowledgeAgentStore — vibe-designer (2026-07-28, Dashboard Build Engine P3).
 *
 * Confirm-gate + revocable auto-approve for AGENT-driven KNOWLEDGE WRITES —
 * currently ingest (POST /api/knowledge/ingest fans out to MIND/CORTEX/OBSIDIAN;
 * Phase 5α). Reads (overview / search) are NOT gated and never touch this store.
 *
 * This is the gate the Build Engine's ingest step uses: a "build my data"
 * conversation reads the tenant's Business Profile (read_business_profile) and
 * proposes an ingest; the write STAGES a pending {description, run} thunk here,
 * and the always-mounted TopBar banner runs it on human Confirm — unless the
 * revocable per-tenant auto-approve toggle is ON. Mirrors n8nAgentStore /
 * payloadAgentStore exactly (single pending slot; refuse to silently replace).
 */
import { create } from 'zustand';

const AUTO_APPROVE_KEY_PREFIX = 'vibe_knowledge_auto_approve_writes_';
const DEFAULT_AUTO_APPROVE = false;

function readStoredAutoApprove(tenantId: string | null): boolean {
  if (!tenantId) return DEFAULT_AUTO_APPROVE;
  try {
    return localStorage.getItem(`${AUTO_APPROVE_KEY_PREFIX}${tenantId}`) === 'true';
  } catch {
    return DEFAULT_AUTO_APPROVE;
  }
}

function writeStoredAutoApprove(tenantId: string | null, value: boolean): void {
  if (!tenantId) return;
  try {
    localStorage.setItem(`${AUTO_APPROVE_KEY_PREFIX}${tenantId}`, value ? 'true' : 'false');
  } catch {
    /* private mode / quota — non-fatal; in-memory state still holds */
  }
}

export interface PendingKnowledgeWrite {
  verb: string;
  description: string;
  run: () => Promise<string>;
  /** Resolves the AGENT-facing gate promise so Pi's tool call returns the real
   * outcome and it continues on its own (no "tell me you confirmed" nudge). */
  resolve: (agentResult: string) => void;
}

interface KnowledgeAgentState {
  autoApproveWrites: boolean;
  pendingWrite: PendingKnowledgeWrite | null;
  lastResult: string | null;
  lastError: string | null;
  initAutoApprove: (tenantId: string | null) => void;
  setAutoApproveWrites: (tenantId: string | null, value: boolean) => void;
  stageWrite: (write: PendingKnowledgeWrite) => void;
  confirmPending: () => Promise<void>;
  cancelPending: () => void;
}

export const useKnowledgeAgentStore = create<KnowledgeAgentState>((set, get) => ({
  autoApproveWrites: DEFAULT_AUTO_APPROVE,
  pendingWrite: null,
  lastResult: null,
  lastError: null,

  initAutoApprove: (tenantId) => set({ autoApproveWrites: readStoredAutoApprove(tenantId) }),

  setAutoApproveWrites: (tenantId, value) => {
    writeStoredAutoApprove(tenantId, value);
    set({ autoApproveWrites: value });
  },

  stageWrite: (write) => set({ pendingWrite: write, lastError: null }),

  confirmPending: async () => {
    const pending = get().pendingWrite;
    if (!pending) return;
    // Clear the card first so a second click can't double-fire the write.
    set({ pendingWrite: null, lastResult: null, lastError: null });
    try {
      const result = await pending.run();
      set({ lastResult: result });
      pending.resolve(`Done (confirmed): ${result}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      set({ lastError: msg });
      pending.resolve(`FAILED (knowledge write): ${msg}`);
    }
  },

  cancelPending: () => {
    const pending = get().pendingWrite;
    if (pending) {
      pending.resolve(
        'CANCELLED by the user — the knowledge write was NOT performed. Do not retry unless the user asks.',
      );
    }
    set({ pendingWrite: null });
  },
}));

/**
 * Shared gate used by the Pi knowledge tools: auto-approve ON runs now and
 * returns the result; otherwise stage for the TopBar Confirm banner. Same
 * semantics as gatedN8nWrite / gatedPayloadWrite (single pending slot — refuse
 * to silently replace an existing one).
 */
export async function gatedKnowledgeWrite(
  verb: string,
  description: string,
  run: () => Promise<string>,
): Promise<string> {
  const st = useKnowledgeAgentStore.getState();
  if (st.autoApproveWrites) {
    try {
      const result = await run();
      useKnowledgeAgentStore.setState({ lastResult: result, lastError: null });
      return `Done (auto-approved): ${result}`;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      useKnowledgeAgentStore.setState({ lastError: msg });
      return `FAILED (auto-approved knowledge write): ${msg}`;
    }
  }
  const existing = st.pendingWrite;
  if (existing) {
    return (
      `NOT STAGED: another knowledge write is already awaiting the user's Confirm — "${existing.description}". ` +
      'Ask the user to Confirm or Cancel that one in the knowledge action banner first, then retry.'
    );
  }
  // Stage AND await the human's decision — the promise stays pending until the
  // inline confirm card's Confirm (→ run() result) or Cancel (→ CANCELLED msg)
  // resolves it, so Pi's tool call returns the ACTUAL outcome and it acts on it.
  return await new Promise<string>((resolve) => {
    st.stageWrite({ verb, description, run, resolve });
  });
}
